import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./PublicationPdfLightbox.css";

const SWIPE_THRESHOLD_PX = 40;

type PublicationPdfLightboxProps = {
  isOpen: boolean;
  title: string;
  pageNumber: number;
  numPages: number;
  canGoPrev: boolean;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

const PublicationPdfLightbox = ({
  isOpen,
  title,
  pageNumber,
  numPages,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
  onClose,
}: PublicationPdfLightboxProps) => {
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const hasMultiple = numPages > 1;

  const goNext = useCallback(() => {
    if (canGoNext) onNext();
  }, [canGoNext, onNext]);

  const goPrev = useCallback(() => {
    if (canGoPrev) onPrev();
  }, [canGoPrev, onPrev]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "ArrowLeft") goPrev();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, goNext, goPrev]);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(event.touches[0].clientX);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX === null || !hasMultiple) return;
    const delta = event.changedTouches[0].clientX - touchStartX;
    if (delta < -SWIPE_THRESHOLD_PX) goNext();
    if (delta > SWIPE_THRESHOLD_PX) goPrev();
    setTouchStartX(null);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="publication-pdf-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — full view`}
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        type="button"
        className="publication-pdf-lightbox-close"
        aria-label="Close full view"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>

      {hasMultiple && (
        <p className="publication-pdf-lightbox-counter" aria-live="polite">
          {pageNumber} / {numPages}
        </p>
      )}

      {hasMultiple && (
        <>
          <button
            type="button"
            className="publication-pdf-lightbox-arrow publication-pdf-lightbox-arrow--prev"
            aria-label="Previous page"
            onClick={(event) => {
              event.stopPropagation();
              goPrev();
            }}
            disabled={!canGoPrev}
          >
            &#8249;
          </button>
          <button
            type="button"
            className="publication-pdf-lightbox-arrow publication-pdf-lightbox-arrow--next"
            aria-label="Next page"
            onClick={(event) => {
              event.stopPropagation();
              goNext();
            }}
            disabled={!canGoNext}
          >
            &#8250;
          </button>
        </>
      )}
    </div>,
    document.body,
  );
};

export default PublicationPdfLightbox;
